import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const BG_URL = "https://images.pexels.com/photos/12331391/pexels-photo-12331391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, user, checking } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (!checking && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-950 text-zinc-100">
      {/* Left visual */}
      <div className="relative hidden md:flex flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG_URL})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/80 via-zinc-950/90 to-black" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute inset-0 bg-noise" />

        <div className="relative z-10">
          <Logo size={48} />
        </div>

        <div className="relative z-10 space-y-6 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-900/70 text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F5A623] pulse-orange" />
            Restricted Access
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Inventory <br />
            <span className="text-[#F5A623]">Command</span> Center
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md">
            Centralized asset tracking, QR labeling, and maintenance scheduling for all KERMA GAMES facilities &mdash; cameras, ATEMs, teleprompters, workstations and more.
          </p>
          <div className="grid grid-cols-3 gap-3 pt-6 border-t border-zinc-800/80">
            {[
              { k: "ASSETS", v: "Tracked" },
              { k: "QR", v: "Instant Scan" },
              { k: "PDF/CSV", v: "Export" },
            ].map((s) => (
              <div key={s.k} className="space-y-1">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{s.k}</div>
                <div className="text-sm text-zinc-200 font-mono">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[10px] tracking-[0.3em] uppercase text-zinc-600">
          © {new Date().getFullYear()} · Authorized Personnel Only
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 relative">
        <div className="absolute inset-0 bg-grid opacity-30 md:hidden" />
        <div className="w-full max-w-sm space-y-8 relative z-10 animate-fade-up">
          <div className="md:hidden"><Logo size={40} /></div>

          <div className="space-y-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">Sign In</div>
            <h2 className="text-3xl font-bold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Welcome back.</h2>
            <p className="text-sm text-zinc-500">Credentials are managed by your administrator.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Email</label>
              <div className="relative">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email"
                  className="w-full pl-9 pr-3 py-2.5 bg-zinc-900/60 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] transition-all"
                  placeholder="name@kermagames.com"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="w-full pl-9 pr-3 py-2.5 bg-zinc-900/60 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] transition-all"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-md px-3 py-2">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm tracking-wide transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Authenticating…" : (<>Sign In <ArrowRight size={16} weight="bold" /></>)}
            </button>
          </form>

          {/* Free-access demo credentials */}
          <div className="rounded-md border border-[#F5A623]/25 bg-[#F5A623]/[0.04] p-3 space-y-2" data-testid="demo-credentials">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F5A623] pulse-orange" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5A623] font-bold">Free Demo Access</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setEmail("demo.admin@kerma.app"); setPassword("DemoAdmin!2026"); }}
                data-testid="demo-admin-fill"
                className="text-left px-2.5 py-2 rounded-md bg-zinc-900/70 border border-zinc-800 hover:border-[#F5A623]/50 hover:bg-zinc-900 transition-colors group"
              >
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-[#F5A623] transition-colors">Admin</div>
                <div className="text-[11px] font-mono text-zinc-300 truncate">demo.admin@kerma.app</div>
              </button>
              <button
                type="button"
                onClick={() => { setEmail("demo.manager@kerma.app"); setPassword("DemoManager!2026"); }}
                data-testid="demo-manager-fill"
                className="text-left px-2.5 py-2 rounded-md bg-zinc-900/70 border border-zinc-800 hover:border-[#F5A623]/50 hover:bg-zinc-900 transition-colors group"
              >
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-[#F5A623] transition-colors">Manager</div>
                <div className="text-[11px] font-mono text-zinc-300 truncate">demo.manager@kerma.app</div>
              </button>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              Tap a role to auto-fill. Then press <span className="text-[#F5A623] font-bold">Sign In</span>.
            </div>
          </div>

          <div className="text-[11px] text-zinc-600 leading-relaxed border-t border-zinc-900 pt-5">
            No public registration. Contact <span className="text-[#F5A623] font-mono">juan.alvarez@kermagames.com</span> to request access.
          </div>
        </div>
      </div>
    </div>
  );
}
